import { FunctionComponent, useState, useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { styles } from '../styles/themes/style';
import DropdownMenu from '../components/dropdown/DropdownMenu';
import DropdownItem from '../components/dropdown/DropdownItem';
import { ITabProperties } from '../components/TabsView';
import Row from '../components/Row';
import Checkbox from '../components/Checkbox';
import Calendar from 'react-calendar';
import Column from '../components/Column';
import FilterDropdownButton from './dropdowns/FilterDropdownButton';
import Text from '../components/Text';
import DropdownSeparator from '../components/dropdown/DropdownSeparator';
import moment from 'moment';
import SvgFragment from './SvgFragment';
import ToggleButton from '../components/ToggleButton';
import { Mail } from 'lucide-react';

export interface ILastMessagedFilterFragmentOption {
    value: string,
    label: ReactNode,
    id: string,
    selected: boolean,
    object: Object
}

interface ILastMessagedFilterFragmentProps {
    children?: React.ReactNode;
    drop?: string
    filterOptions: ILastMessagedFilterFragmentOption[];
    selectedOwners: any[];
    inputTitle: string;
    showSearchField?: boolean;
    inputSelect?: boolean;
    resetDisabled?: boolean;
    lastMessagedFrom?: any;
    lastMessagedTo?: any;
    conversationStatus?: any;
    conversationOwner?: any;
    isBusinessWs?: boolean;
    upgradePlan?: boolean
    showOnlyLast?: boolean;
    onSelectedOwnerHandler: (control: any, newValue: any) => void;
    onSelectedDateHandler: (control: any, newValue: any) => void;
    onStatusChangeHandler: (control: any, newValue: any) => void;
    onOnlyLastToggleHandler: (control: any, newValue: any) => void;
    resetFilter: () => void;
}

const LastMessagedFilterFragment: FunctionComponent<ILastMessagedFilterFragmentProps> = ({ drop, filterOptions, selectedOwners, inputTitle, lastMessagedFrom, lastMessagedTo, conversationStatus, showOnlyLast, isBusinessWs=true, upgradePlan, onSelectedOwnerHandler, onSelectedDateHandler, onStatusChangeHandler, resetFilter }) => {
 
    const [selectOptions, setSelectOptions] = useState<ILastMessagedFilterFragmentOption[]>([]);
    const [dateFrom, setDateFrom] = useState(lastMessagedFrom);
    const [dateTo, setDateTo] = useState(lastMessagedTo);
    const [onlyLast, setOnlyLast] = useState(showOnlyLast);
    const [selectedNames, setSelectedNames] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>(conversationStatus);
    const [selectedAction, setSelectedAction] = useState<string | null>(null);

    const upgradePlanTooltip = 'Upgrade your plan to unlock this feature';

    const hasValue = !!(dateFrom || dateTo || selectedStatus || selectedOwners?.length || onlyLast)
    const dropdownButton = <FilterDropdownButton
        icon={<Mail size={16} className="filter-field-icon" />}
        upgradePlan={upgradePlan}
        label={inputTitle}
        active={hasValue}
        value={dateFrom || dateTo ? <>
            {dateFrom ? moment(dateFrom).format('MMM D, YYYY') : 'From Date'}
            {' - '}
            {dateTo ? moment(dateTo).format('MMM D, YYYY') : 'To Date'}
            </> : (selectedStatus || (selectedOwners?.length ? `${selectedOwners.length} Selected` : (onlyLast ? 'Last messaged only' : 'Any time')))}
        resetFilter={hasValue ? (e: any) => {e?.stopPropagation(); resetFilter()} : undefined}
    />

    const dropdownButtonMembers = <FilterDropdownButton
        placeholder={selectedNames || 'All members'}
        />

    const dropdownButtonStatus = <FilterDropdownButton
        placeholder={selectedStatus || 'All conversations'}
        />

    useEffect(() => {
        if(!selectOptions.length && filterOptions.length){
            setSelectOptions([
                {id: 'all', label: 'All Members', selected: false, value: 'all', object: {}}, 
                ...filterOptions?.map((option) => (
                    {...option, selected: selectedOwners?.some((item) => item.id === option.id)}
                ))
            ]);
        } 
    }, [filterOptions]);

    useEffect(() => {
        setDateFrom(lastMessagedFrom);
    }, [lastMessagedFrom]);
 
    useEffect(() => {
        setDateTo(lastMessagedTo);
    }, [lastMessagedTo]);

    useEffect(() => {
        if(conversationStatus){
            const statusLabel = statusOptions.find((item: any) => item?.value === conversationStatus)?.label;
            setSelectedStatus(statusLabel || '');
        } else {
            setSelectedStatus('');
        }
    }, [conversationStatus]);

    useEffect(() => {
        // if filters are cleared - reset selection
        if(!selectedOwners && selectOptions){
            setSelectOptions(selectOptions?.map((option: any) => (
                {...option, selected: false}
            )));
        }
    }, [selectedOwners]);

    useEffect(() => {
        if(selectOptions){
            // re-render owners selection status
            if(selectedOwners?.length){
                if (selectedOwners.filter((item: any) => item.id !== 'all').length <= 1) {
                    setSelectedNames(selectedOwners.filter((item: any) => item.id !== 'all').map((o: any) => o.value).join(', '));
                } else {
                    setSelectedNames(`Members: ${selectedOwners.filter((item: any) => item.id !== 'all').length} selected`);
                }
            } else {
                setSelectedNames('All members');
            }
        }
    }, [selectOptions]);
    
    useEffect(() => {
        setOnlyLast(!!showOnlyLast);
    }, [showOnlyLast]);

    const toggleItemHandler = (event: any, item: ILastMessagedFilterFragmentOption) => {
        event.stopPropagation()
        let add: boolean
        let allSelected = false;
        let selectedOwnersObj: any = [];
        if(item.id === 'all'){
            add = !item.selected
            setSelectOptions(selectOptions.map((option) => {
                if (add){
                    selectedOwnersObj.push(option);
                    allSelected = true;
                }   
                return {...option, selected: add}
            }))
        } else {
            setSelectOptions(selectOptions.map((option) => {
                if(option.id === item.id) {
                    add = !option.selected
                    let newOption = {...option, selected: add}
                    if(newOption.selected) {
                        selectedOwnersObj.push(option);
                    }
                    return newOption;
                } else if(option.selected) {
                    selectedOwnersObj.push(option);
                }
                if(item.selected){
                    allSelected = false;
                }
                return {...option, selected: (option.id === 'all' && item.selected) ? false : option.selected}
            }))
        }

        if(!allSelected){ // remove 'all' if not all members are selected
            selectedOwnersObj = selectedOwnersObj.filter((item: any) => item.id !== 'all');
        }

        if(item.id !== 'all'){
            if (selectedOwnersObj.filter((item: any) => item.id !== 'all').length <= 1) {
                setSelectedNames(selectedOwnersObj.filter((item: any) => item.id !== 'all').map((o: any) => o.value).join(', '));
              } else {
                setSelectedNames(`Members: ${selectedOwnersObj.filter((item: any) => item.id !== 'all').length} selected`);
              }
        } else {
            setSelectedNames('All members');
        }

       onSelectedOwnerHandler('conversationOwners', selectedOwnersObj.length ? selectedOwnersObj : undefined)
    }

    const dateChangedHandler = (fromOrTo: 'from' | 'to' | 'actionText', event: any) => {
        if (fromOrTo === 'from') {
            onSelectedDateHandler('lastMessagedFrom', event[0]);
        } else if (fromOrTo === 'to') {
            onSelectedDateHandler('lastMessagedTo', event[1]);
        } else {
            setSelectedAction(event?.value);
            if(event?.value === actionValuesObjects[0].value || event?.value === actionValuesObjects[1].value){ // today or yesterday
                const time = moment(event?.date);
                onSelectedDateHandler(['lastMessagedFrom', 'lastMessagedTo'], [time.startOf('day').toDate(), time.endOf('day').toDate()]);
            } else {
                const firstDayOfMonth = moment(event?.date);
                const lastDayOfMonth = firstDayOfMonth.endOf('month').toDate();
                onSelectedDateHandler(['lastMessagedFrom', 'lastMessagedTo'], [event?.date, lastDayOfMonth]);
            }
        }
    };

    const onConversationStatusChangeHandler = (control: any, newValue: any) => {
        const statusLabel = statusOptions.find((item: any) => item?.value === newValue)?.label;
        setSelectedStatus(statusLabel || '');
        onStatusChangeHandler && onStatusChangeHandler(control, newValue);
    }

    const statusOptions = [
        { value: 'all', label: 'All conversations' },
        { value: 'conversationsStarted', label: 'Conversation started' },
        { value: 'lastMessageOutgoing', label: 'Last message outgoing' },  
        { value: 'lastMessageIncoming', label: 'Last message incoming' },  
        { value: 'noConversationRecorded', label: 'No conversation recorded' },  
    ];

    const actionValuesObjects = [
        {
            value: 'Today',
            date: moment().add(0, 'day').toDate()
        },
        {
            value: 'Yesterday',
            date: moment().add(-1, 'day').toDate()
        },
        {
            value: 'This month',
            date: moment().startOf('month').toDate()
        },
        {
            value: 'Last month',
            date: moment().subtract(1, 'month').startOf('month').toDate()
        }   
    ]

    const onlyLastClickHandler = (e: any, isToggled: boolean) => {
        setOnlyLast(isToggled);
        onSelectedDateHandler('onlyLast', isToggled);
    };

    return (
        <div>
        {upgradePlan ? <UpgradePlanWrapper alignItems>{dropdownButton}<SvgFragment type='dropdownArrow' /></UpgradePlanWrapper> :
            <DropdownMenu 
                    title={dropdownButton}
                    autoClose='outside'
                    drop={drop}
                    selectStyle
                    $hideToggle={dateFrom || dateTo}
                >   
                <DropdownItem $nonHoverable>
                    <Row>
                        <Actions>
                            {actionValuesObjects.map((v: any, index: number) => 
                                <ActionText 
                                    isSelected={selectedAction === v.value} 
                                    key={index} 
                                    onClick={() => dateChangedHandler('actionText', v)}>{v.value}
                                </ActionText>)}
                        </Actions>
                    </Row>
                    <DropdownSeparator />
                    <Row style={{marginBottom: '10px'}}>
                        <ToggleButton
                            flex={true}
                            value={onlyLast}
                            label='Show last messaged only'
                            tooltipMessageOn={'Turn last messaged only on'}
                            tooltipMessageOff={'Turn last messaged only off'}
                            onClickHandler={onlyLastClickHandler} />
                    </Row>
                    <Row>
                        <LeftSide>
                            <Label>From Date</Label>
                            <StyledCalendar
                                value={!dateTo ? dateFrom : [dateFrom, dateTo]}
                                onChange={event => dateChangedHandler('from', event as Date[])}
                                showNeighboringMonth={false}
                                minDate={new Date(1900, 4, 5)}
                                maxDate={dateTo || new Date()}
                                returnValue='range'
                                defaultActiveStartDate={!dateFrom ? new Date(new Date().getFullYear(), new Date().getMonth() - 1, new Date().getDate()) : dateFrom}
                                locale='en-US' 
                            />
                        </LeftSide>
                        <RightSide>
                            <Label>To Date</Label>
                            <StyledCalendar
                                value={!dateTo ? null : [dateFrom, dateTo]}
                                onChange={event => dateChangedHandler('to', event as Date[])}
                                showNeighboringMonth={false}
                                minDate={dateFrom || new Date(1900, 4, 5)}
                                maxDate={new Date(2050, 4, 5)}
                                returnValue='range'
                                defaultActiveStartDate={dateTo && dateTo}
                                locale='en-US' 
                            />
                        </RightSide>
                    </Row>
                    <DropdownSeparator />
                    <Row alignItems gap="12px">
                        <StyledColumnLeft
                            onClick={(e) => {
                                e.stopPropagation();
                            }}>
                            <TitleContainer>
                                <Label isBusiness={isBusinessWs}>Conversation status</Label>
                                {!isBusinessWs && <SvgFragment type='lock'/>}
                            </TitleContainer>
                            <StyledDropdownMenu
                                title={isBusinessWs ? dropdownButtonStatus : <StyledTitleContainer>{'All conversations'}</StyledTitleContainer>}
                                drop='down'
                                selectStyle
                                disabled={!isBusinessWs}
                                tooltipMessage={!isBusinessWs ? upgradePlanTooltip : ''}
                                isBusiness={isBusinessWs}
                            >  
                                    {statusOptions.map((item: any) => (
                                        <DropdownItem
                                            key={item.id}
                                            onClickHandler={(e: any) => onConversationStatusChangeHandler('conversationStatus', item.value)}>
                                            <Row alignItems gap="10px">
                                                <Column>
                                                    {<Text>{item.label}</Text>}
                                                </Column>
                                            </Row>
                                    </DropdownItem>
                                    ))}
                            </StyledDropdownMenu>
                        </StyledColumnLeft>
                        <StyledColumnRight>
                            <TitleContainer>
                                <Label isBusiness={isBusinessWs}>Conversation owner</Label>
                                {!isBusinessWs && <SvgFragment type='lock'/>}
                            </TitleContainer>
                            <StyledDropdownMenu
                                title={isBusinessWs ? dropdownButtonMembers : <StyledTitleContainer>{'All members'}</StyledTitleContainer>}
                                autoClose='outside'
                                drop='down'
                                selectStyle
                                disabled={!isBusinessWs}
                                tooltipMessage={!isBusinessWs ? upgradePlanTooltip : ''}
                                isBusiness={isBusinessWs}
                            >  
                                    {selectOptions.map((item: any) => (
                                        <DropdownItem
                                            key={item.id}
                                            onClickHandler={(e: any) => toggleItemHandler(e, item)}>
                                            <Row alignItems gap="10px">
                                                <Checkbox checked={item.selected} checkboxId={item.id} />
                                                {item.label}
                                            </Row>
                                    </DropdownItem>
                                    ))}
                            </StyledDropdownMenu>
                        </StyledColumnRight>
                    </Row>
                </DropdownItem>
            </DropdownMenu>
            }
        </div>
    );
}

const UpgradePlanWrapper = styled(Row)`
    min-width: 296px;
    border: 2px solid ${styles.colors.black200};
    border-radius: 4px;
    padding-left: 6px;
    background-color: ${styles.colors.black100};
`

const StyledDropdownMenu = styled(DropdownMenu)<{ isBusiness?: boolean }>`
    background-color: transparent !important;
    .actions-dropdown + div {
        margin-bottom: 13px;
        width: 100%;
        max-height: 350px;
        overflow-x: hidden !important;
    }

    .actions-dropdown > span {
        color: ${styles.colors.disabledState} !important;
    }

    /* keep the nested pickers looking like the fields in the sidebar, not like link buttons */
    .dropdownWrapperReference {
        font-weight: 400;
        color: ${styles.colors.black600};
    }

    .dropdown-toggle {
        outline: none;
        box-shadow: none;

        &:hover, &:focus {
            outline: none;
            box-shadow: none;
        }
    }
`

const StyledColumnLeft = styled(Column)`
    width: 100%;
    min-width: 0;
    margin-top: 6px;
    margin-bottom: 12px;
`;

const StyledColumnRight = styled(Column)`
    width: 100%;
    min-width: 0;
    margin-top: 6px;
    margin-bottom: 12px;
`;

const Side = styled.div`
    width: 240px;
    display: inline-block;
    vertical-align: top;
`;

const LeftSide = styled(Side)`
    margin-right: 27px;
`;

const RightSide = styled(Side)``;

const TitleContainer = styled.div`
    display: flex;
    width: 100%;
    gap: 4px;
    align-items: center;
    margin-bottom: 8px;
`

const StyledTitleContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    height: 34px;
    border: none;
    width: 100%;
    border-radius: 4px;
    cursor: pointer;
    color: ${styles.colors.disabledState}
`

const Label = styled.p<{ isBusiness?: boolean }>`
    line-height: 16px;
    font-family: NunitoSans;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({isBusiness}) => isBusiness ? styles.colors.black600 : styles.colors.black300 };
    margin: 0;
`;

const StyledCalendar = styled(Calendar)`
    width: 240px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: bold;
    color: ${styles.colors.black600};

    & abbr { text-decoration: none; }

    & button {
        border: none;
        background-color: transparent;

        &:disabled, &:disabled:hover { color: ${props => props.theme.primary ? '#b8bfcf' : '#7e8087'}; }
        &:not(:disabled) {
            color: ${props => props.theme.primary ? '#333951' : '#E8E8E8'};
            cursor: pointer;
        }       
    }

    & .react-calendar__navigation {
        height: 20px;
        margin-bottom: 10px;
        display: flex;
    }

    & .react-calendar__navigation__label {
        font-weight: bold;
        color: ${styles.colors.black600};
        display: flex;
        justify-content: center;
    }

    & .react-calendar__navigation__arrow {
        line-height: 20px;
        font-size: 20px;
        font-weight: bold;
        color: #8c96ad;
    }

    & .react-calendar__viewContainer {
        min-height: 204px;
    }

    & .react-calendar__month-view__weekdays {
        height: 20px;
        margin-bottom: 4px;
        align-items: center;
    }

    & .react-calendar__month-view__weekdays__weekday {
        text-align: center;
    }

    & .react-calendar__month-view__days {
        /* 6 rows of 30px so months that span 6 weeks are fully visible without scrolling */
        min-height: 180px;
    }

    & .react-calendar__tile {
        height: 28px;
        width: 33px;
        margin-bottom: 2px;

        &:hover {color: #5964FF;}
    }

    & .react-calendar__tile--active {        
        background-color: ${props => props.theme.primary ? '#eef3ff' : '#373945'};
        color: ${props => props.theme.primary ? '#333951 !important' : '#E8E8E8 !important'};

        &:hover { background-color: ${props => props.theme.primary ? '#F6FBFF' : '#2A2D3A'}; }
    }

    & .react-calendar__tile--rangeStart, & .react-calendar__tile--rangeEnd {
        border-radius: 6px;
        background-color: #5964ff;
        color: ${props => props.theme.primary ? 'white !important' : '#E8E8E8 !important'};

        &:hover {
            background-color: #7a89fc;
            color: ${props => props.theme.primary ? '#333951' : '#E8E8E8'};
        }
    }
`

const Actions = styled.div`
    display: flex;
    width: 100%;
    height: 36px;
    gap: 10px;
    align-items: center;
`

const ActionText = styled.span<{ isSelected: boolean }>`
    color: ${styles.colors.primary600};
    background-color: #EEF3FA;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    border: 1px solid transparent;
    &:hover { border: 1px solid #5964FF !important; }
    ${({ isSelected }) =>
        isSelected && `border: 1px solid ${styles.colors.primary600};`}
`

export default LastMessagedFilterFragment;
